import { IConfirmCloneOrMoveModal } from "./../../../goals/controllers/clone-or-move-goal";
import { IPromise, IHttpService, IRequestShortcutConfig, IRootScopeService } from "angular";

import { AnalyticsService } from "@gtmhub/core/analytics";
import { EnvironmentService } from "@gtmhub/env";
import { ICheckinComment, IGoal } from "@gtmhub/goals/models";
import { ICfMap } from "@gtmhub/customFields/models/models";
import { ICollection } from "@gtmhub/models";
import { IInsightSelectorData } from "@gtmhub/insights";
import { IMetric, ICheckin, IMetricsQueryParamsOptions, ICheckinV2, MetricFormEventType, MetricFormState, MetricType } from "../models/models";
import { OkrEvents } from "@gtmhub/okrs";
import { GoalModalMethod } from "@gtmhub/goals/goalModalsService";
import { IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import { TaskService } from "@gtmhub/tasks";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IRestLayerRequest } from "@gtmhub/core";
import { PluginGtmhubAdditionalParams } from "@gtmhub/plugins/components/plugin/plugin.component";
import { AssigneeService } from "@gtmhub/assignees/services";
import { IPlanningSession } from "@gtmhub/sessions";

type MethodType = "clone" | "move";

export const metricProperties = [
  "name",
  "insightName",
  "fieldName",
  "targetOperator",
  "target",
  "critical",
  "initialValue",
  "actual",
  "manualType",
  "softDueDate",
  "dueDate",
  "description",
  "ownerId",
  "ownerIds",
  "customFields",
  "format",
  "cascadeType",
  "settings",
];

export class MetricService {
  private okrEvents: OkrEvents;

  public static $inject = ["$http", "$rootScope", "AnalyticsService", "EnvironmentService", "$uibModal", "TaskService", "UIErrorHandlingService", "AssigneeService"];

  constructor(
    private $http: IHttpService,
    private $rootScope: IRootScopeService,
    private analyticsService: AnalyticsService,
    private env: EnvironmentService,
    private $uibModal: IModalService,
    private tasksService: TaskService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeService: AssigneeService
  ) {
    this.okrEvents = new OkrEvents($rootScope);
  }

  public deleteMetric(goalId: string, metricId: string, additionalGtmhubParams?: PluginGtmhubAdditionalParams): IPromise<void> {
    const url = this.env.getApiEndpoint(`/metrics/${metricId}`);

    const config: IRequestShortcutConfig = {
      params: additionalGtmhubParams,
    };

    return this.$http.delete<void>(url, config).then(() => this.okrEvents.broadcastGoalsChanged("metricDeleted", { goalId, metricId }));
  }

  public checkin(metric: IMetric, checkin: ICheckin, additionalGtmhubParams?: PluginGtmhubAdditionalParams): IPromise<ICheckin> {
    const url = this.env.getApiEndpoint(`/metrics/${metric.id}/checkin`);
    const gtmhubAdditionalParams: IRequestShortcutConfig = {
      params: {
        gtmhubAdditionalParams: {
          ...additionalGtmhubParams,
          hasGif: !!checkin.gif?.id,
        },
      },
    };
    return this.$http.post<ICheckin>(url, checkin, gtmhubAdditionalParams).then((response) => {
      const newCheckin = response.data;
      this.okrEvents.broadcastGoalsChanged("metricCheckin", { goalId: metric.goalId, metricId: metric.id, checkin: newCheckin });
      return newCheckin;
    });
  }

  public patchMetric(metric: Partial<IMetric>, additionalGtmhubParams?: PluginGtmhubAdditionalParams): IPromise<IMetric> {
    const url = this.env.getApiEndpoint(`/metrics/${metric.id}`);

    const config: IRequestShortcutConfig = {
      params: additionalGtmhubParams,
    };

    return this.$http.patch<IMetric>(url, metric, config).then((response) => {
      const patchedMetric = response.data;
      this.okrEvents.broadcastGoalsChanged("metricPatched", { goalId: patchedMetric.goalId, metric: patchedMetric, patch: metric });
      return patchedMetric;
    });
  }

  public patchMetricComment(metricHistoryId: string, comment: ICheckinComment): IPromise<string> {
    const url = this.env.getApiEndpoint(`/metrics/checkin/${metricHistoryId}`);
    const gtmhubAdditionalParams: IRequestShortcutConfig = {
      params: {
        gtmhubAdditionalParams: {
          hasGif: !!comment.gif.id,
        },
      },
    };

    return this.$http.patch<string>(url, comment, gtmhubAdditionalParams).then((response) => response.data);
  }

  public deleteSnapshot(goalId: string, metricId: string, snapshotId: string): IPromise<void> {
    const url = this.env.getApiEndpoint(`/metrics/checkin/${snapshotId}`);

    return this.$http.delete<void>(url).then(() => this.okrEvents.broadcastGoalsChanged("metricSnapshotDeleted", { goalId, metricId, snapshotId }));
  }

  public getMetric(metricId: string, additionalGtmhubParams?: PluginGtmhubAdditionalParams): IPromise<IMetric> {
    const url = this.env.getApiEndpoint(`/metrics/${metricId}`);

    const config: IRequestShortcutConfig = {
      params: additionalGtmhubParams,
    };

    return this.$http.get<IMetric>(url, config).then((response) => response.data);
  }

  public getMetricByCheckin(checkinId: string): IPromise<IMetric> {
    const url = this.env.getApiEndpoint(`/metrics/checkin/${checkinId}`);

    return this.$http.get<IMetric>(url).then((response) => response.data);
  }

  public getMetrics(queryParams?: IMetricsQueryParamsOptions): IPromise<ICollection<IMetric>> {
    const url = this.env.getApiEndpoint("/metrics");
    const query: IRequestShortcutConfig = {
      params: queryParams,
    };

    return this.$http.get<ICollection<IMetric>>(url, query).then((response) => response.data);
  }

  public getMetricsV2(params: IRestLayerRequest, gtmhubAdditionalParams?: PluginGtmhubAdditionalParams): IPromise<ICollection<IMetric>> {
    const config: IRequestShortcutConfig = {
      params: {
        ...params,
        ...gtmhubAdditionalParams,
      },
    };

    const url = this.env.getApiEndpointV2("/metrics");
    return this.$http.get<ICollection<IMetric>>(url, config).then((response) => response.data);
  }

  public getCheckinsV2(params: IRestLayerRequest): IPromise<ICollection<ICheckinV2>> {
    const url = this.env.getApiEndpointV2("/metrics/checkins");
    return this.$http.get<ICollection<ICheckinV2>>(url, { params }).then((response) => response.data);
  }

  public isFieldPartOfWorkflow(fieldName: string, session?: IPlanningSession): boolean {
    return session?.workflow?.editableKrFields?.includes(fieldName);
  }

  public trackMetricForm(event: MetricFormEventType, metric: IMetric): void {
    const meta = {
      type: metric && metric.id ? MetricFormState.EDIT : MetricFormState.CREATE,
      metric_type: metric && metric.insightName !== "" ? MetricType.DYNAMIC : MetricType.MANUAL,
    };
    this.analyticsService.track(event, meta);
  }

  public generateInsightSelectorData(metric: IMetric): IInsightSelectorData {
    return {
      settings: metric.settings,
      insightName: metric.insightName,
      fieldName: metric.fieldName,
      initialValue: metric.initialValue,
    };
  }

  public updateMetricInsightData(
    metric: IMetric,
    data: IInsightSelectorData,
    isNumberFormattingFeatureEnabled: boolean,
    shouldPredict?: { [key: string]: boolean }
  ): void {
    metric.settings = data.settings;
    if (data.initialValue != null) {
      metric.initialValue = data.initialValue;
      if (shouldPredict) {
        shouldPredict["initialValue"] = false;
      }
    }
    metric.insightName = data.insightName;
    metric.fieldName = data.fieldName;
    metric.manualType = metric.insightName ? "" : "double";
    // set the actual of a new metric
    // because sometimes it may keep the value of insight added via IB and then changed
    if (!metric.id) {
      metric.actual = data.initialValue;
    }

    if (isNumberFormattingFeatureEnabled && data.formatting) {
      //init metric format again because to prediction sets it to null when we dont have a prediction,
      // if we have prediction we should override it with the data from the insight
      metric.format = {
        prefix: "",
        suffix: "",
        fractionSize: 0,
      };

      metric.format.suffix = data.formatting.suffix ? data.formatting.unit : "";
      metric.format.prefix = data.formatting.suffix ? "" : data.formatting.unit;
    }
  }

  public createMetric(metric: IMetric, additionalGtmhubParams?: PluginGtmhubAdditionalParams | Record<string, unknown>): IPromise<IMetric> {
    const url = this.env.getApiEndpoint("/metrics");

    const config: IRequestShortcutConfig = {
      params: additionalGtmhubParams,
    };

    return this.$http.post<IMetric>(url, metric, config).then((response) => {
      const newMetric = response.data;
      this.okrEvents.broadcastGoalsChanged("metricCreated", { goalId: newMetric.goalId, metric: newMetric });
      return newMetric;
    });
  }

  public cloneOrMoveMetric(metric: IMetric, goal: IGoal, method: MethodType): void {
    const m = metric.customFields as ICfMap;
    const cfCounter = m ? Object.keys(m).length : 0;

    if (method === "clone") {
      // check if we have deactivated owners and remove them
      metric.ownerIds = this.assigneeService.getActiveAssigneesOnly(metric.ownerIds);
    }

    if (cfCounter != 0) {
      this.confirmCloneOrMoveMetric(metric, goal, method, cfCounter);
    } else {
      this.prepareToCloneOrMoveMetric(metric, goal, method);
    }
  }

  private confirmCloneOrMoveMetric(metric: IMetric, goal: IGoal, method: MethodType, cfCounter: number) {
    const invokerType = "Key Result";

    this.$uibModal
      .open({
        templateUrl: require("../../../goals/views/modules/confirm-clone-or-move.jade"),
        windowClass: "medium-small",
        controller: [
          "$scope",
          function ($scope: IConfirmCloneOrMoveModal) {
            $scope.method = method;
            $scope.cfCounter = cfCounter;
            $scope.invokerType = invokerType;
          },
        ],
      })
      .result.then(() => {
        this.prepareToCloneOrMoveMetric(metric, goal, method);
      });
  }

  private prepareToCloneOrMoveMetric(metric: IMetric, goal: IGoal, method: MethodType): void {
    if (method === "move") {
      this.moveMetric(metric, goal);
    } else {
      this.cloneMetric(metric, goal);
    }
  }

  private cloneMetric(metric: IMetric, goal: IGoal): void {
    const newMetric = { ...metric, goalId: goal.id };
    delete newMetric.id;
    delete newMetric.confidence;

    this.createMetric(newMetric).then(
      (clonedMetric: IMetric) => {
        const limitV2 = 100;
        this.tasksService.cloneTasksRecursively({ currentPage: 1, limit: limitV2 }, { newParentIdsMap: { [metric.id]: clonedMetric.id }, parentIds: [metric.id] }).then(
          () => {
            // these goal changed broadcasts will be removed with the refactoring of the controllers
            this.okrEvents.broadcastGoalsChanged("goalChanged");
            this.$rootScope.$broadcast("goalsChanged");

            this.successfullyClonedOrMovedMetric(goal, "clone");
          },
          (error) => this.uiErrorHandlingService.handleModal(error)
        );
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  }

  private moveMetric(metric: IMetric, goal: IGoal): void {
    const metricForPatch: Partial<IMetric> = { id: metric.id, goalId: goal.id };
    const oldGoalId = metric.goalId;
    // if there is an error the user will just not get the success modal
    this.patchMetric(metricForPatch).then(() => {
      this.okrEvents.broadcastGoalsChanged("goalChanged");
      this.$rootScope.$broadcast("goalsChanged");
      this.okrEvents.broadcastGoalsChanged("metricMoved", { goalId: oldGoalId, metric: { ...metric, goalId: goal.id } });

      this.successfullyClonedOrMovedMetric(goal, "move");
    });
  }

  public filterWorkflowSelectionProperties(editableKrFields: string[]): string[] {
    return metricProperties.filter((property) => editableKrFields.includes(property) || property === "customFields");
  }

  public successfullyClonedOrMovedMetric(goal: IGoal, method: GoalModalMethod): IModalServiceInstance {
    return this.$uibModal.open({
      templateUrl: require("../../../goals/views/modules/cloned-or-moved-metric-success.jade"),
      windowClass: "medium",
      controller: [
        "$scope",
        function ($scope) {
          $scope.goal = goal;
          $scope.method = method;
        },
      ],
    });
  }
}
